import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiFailure, ERROR_COPY, type GameState } from "./api";
import { getInitData, haptic } from "./telegram";

export type Screen<V> =
  | { kind: "loading" }
  | { kind: "error"; message: string; retry: boolean }
  | { kind: "game"; state: GameState<V> };

export interface UseGameOptions {
  /** How long a finishing move waits before the result sheet (default 600ms) — room for a game's own finale. */
  sheetDelayMs?: number;
}

/** Shared loading / submit / toast plumbing for every game screen. `date` = archive replay. */
export function useGame<V>(game: string, errorCopy: Record<string, string> = {}, date?: string, opts: UseGameOptions = {}) {
  const sheetDelayMs = opts.sheetDelayMs ?? 600;
  const [screen, setScreen] = useState<Screen<V>>({ kind: "loading" });
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const toastTimer = useRef<number | null>(null);

  const showToast = useCallback((msg: string, ms = 1600) => {
    setToast(msg);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), ms);
  }, []);

  const load = useCallback(async () => {
    setScreen({ kind: "loading" });
    if (!getInitData()) {
      setScreen({ kind: "error", message: ERROR_COPY.unauthorized ?? "", retry: false });
      return;
    }
    try {
      const state = await api.state<V>(game, date);
      setScreen({ kind: "game", state });
      if (state.status !== "playing") setSheetOpen(true);
    } catch (e) {
      const code = e instanceof ApiFailure ? e.code : "server_error";
      setScreen({
        kind: "error",
        message: ERROR_COPY[code] ?? ERROR_COPY.server_error ?? "",
        retry: code !== "unauthorized" && code !== "game_disabled",
      });
    }
  }, [game, date]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = useCallback(
    async (fn: () => Promise<GameState<V>>): Promise<GameState<V> | null> => {
      if (busy) return null;
      setBusy(true);
      try {
        const next = await fn();
        setScreen({ kind: "game", state: next });
        if (next.status !== "playing") {
          if (next.status === "won") haptic.success();
          else haptic.error();
          window.setTimeout(() => setSheetOpen(true), sheetDelayMs);
        } else {
          haptic.tap();
        }
        return next;
      } catch (e) {
        const code = e instanceof ApiFailure ? e.code : "server_error";
        const status = e instanceof ApiFailure ? e.status : 0;
        if (code === "game_over") void load();
        else {
          haptic.error();
          const known = errorCopy[code] ?? ERROR_COPY[code];
          showToast(known ?? `${ERROR_COPY.server_error ?? ""} (${code}${status ? ` ${status}` : ""})`, 2200);
        }
        return null;
      } finally {
        setBusy(false);
      }
    },
    [busy, load, showToast, errorCopy, sheetDelayMs],
  );

  /** Accept a state the screen fetched itself (custom submit flows). */
  const apply = useCallback((next: GameState<V>) => setScreen({ kind: "game", state: next }), []);

  return { screen, busy, toast, sheetOpen, setSheetOpen, load, run, showToast, apply };
}
